import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/content/blog`).then(r => r.json()),
      fetch(`${API_URL}/api/content/site`).then(r => r.json()),
    ]).then(([posts, site]) => {
      setBlogPosts(posts);
      if (site?.blogCategories) setCategories(site.blogCategories);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filteredPosts = selectedCategory === 'All'
    ? blogPosts
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPosts = blogPosts.filter(post => post.featured).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-900 text-white py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-serif mb-6">Trailz Travel Blog</h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Expert guides, travel tips, and adventure stories from the Himalayas
          </p>
        </div>
      </section>

      <section className="bg-zinc-50 py-8 px-4 border-b border-zinc-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-3 overflow-x-auto pb-2">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={`rounded-sm whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-emerald-800 text-white hover:bg-emerald-900'
                    : 'border-2 border-zinc-300 text-zinc-700 hover:border-emerald-800 hover:text-emerald-800'
                }`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-700" />
        </div>
      ) : (
        <>
          {selectedCategory === 'All' && featuredPosts.length > 0 && (
            <section className="py-16 px-4 bg-white">
              <div className="max-w-7xl mx-auto">
                <div className="mb-12">
                  <h2 className="text-4xl font-serif mb-2 text-zinc-900">Featured Stories</h2>
                  <div className="w-20 h-1 bg-amber-600" />
                </div>
                <div className="grid md:grid-cols-3 gap-8">
                  {featuredPosts.map((post) => (
                    <Link key={post.post_id || post.slug} to={`/blog/${post.slug}`}>
                      <Card className="group overflow-hidden hover:shadow-2xl transition-all duration-500 border-0 rounded-sm h-full">
                        <div className="relative h-64 overflow-hidden">
                          <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                          <div className="absolute top-4 right-4 bg-amber-600 text-white px-3 py-1 text-sm font-semibold rounded-sm">Featured</div>
                        </div>
                        <CardContent className="p-6 bg-zinc-50">
                          <div className="flex items-center gap-4 text-sm text-zinc-500 mb-3">
                            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{post.readTime}</span>
                          </div>
                          <h3 className="text-2xl font-serif text-zinc-900 mb-3 group-hover:text-emerald-800 transition-colors">{post.title}</h3>
                          <p className="text-zinc-600 mb-4 line-clamp-2">{post.excerpt}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-emerald-700 font-medium">{post.category}</span>
                            <ArrowRight className="w-5 h-5 text-emerald-700 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="py-16 px-4 bg-zinc-50">
            <div className="max-w-7xl mx-auto">
              <div className="mb-12">
                <h2 className="text-4xl font-serif mb-2 text-zinc-900">
                  {selectedCategory === 'All' ? 'All Stories' : selectedCategory}
                </h2>
                <div className="w-20 h-1 bg-amber-600" />
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post) => (
                  <Link key={post.post_id || post.slug} to={`/blog/${post.slug}`}>
                    <Card className="group overflow-hidden hover:shadow-xl transition-all duration-500 border-0 rounded-sm h-full">
                      <div className="relative h-56 overflow-hidden">
                        <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute top-4 left-4 bg-emerald-800 text-white px-3 py-1 text-xs font-semibold rounded-sm">{post.category}</div>
                      </div>
                      <CardContent className="p-6 bg-white">
                        <div className="flex items-center gap-4 text-xs text-zinc-500 mb-3">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
                        </div>
                        <h3 className="text-xl font-serif text-zinc-900 mb-2 group-hover:text-emerald-800 transition-colors line-clamp-2">{post.title}</h3>
                        <p className="text-zinc-600 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                        <div className="flex items-center text-emerald-700 text-sm font-medium">
                          Read More <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default Blog;
